import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Button,
  Modal,
  Tooltip,
  Input,
  Badge,
  Space,
  Avatar,
  Form,
  Typography,
  Dropdown,
  Tabs,
} from "antd";
import {
  PhoneOutlined,
  LoginOutlined,
  LogoutOutlined,
  UserSwitchOutlined,
  UserOutlined,
  CoffeeOutlined,
  MoreOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  StopOutlined,
  PhoneFilled,
  AudioMutedOutlined,
  AudioOutlined,
  CloseOutlined,
  UpCircleOutlined,
  DownCircleOutlined,
} from "@ant-design/icons";
import { useGet } from "@shesha-io/reactjs";
import { useTelephonyContext } from "./telephonyContext";
import { CallDirection, CallStatus } from "../../models/index";
import { normalizePhoneNumber } from "../../providers/utils/phoneUtils";
import { cloneDeep } from "lodash";

const { Text } = Typography;

export interface IShaTelephonyProps {
  providerType: "innovaphone" | "awsconnect" | "signalr";
  showDialpad?: boolean;
  enableRejectCall?: boolean;
  showAvailabilityToggle?: boolean;
  config?: any;
  onCallStarted?: (callDetails: any) => void;
  onCallEnded?: (callDetails: any) => void;
  onAgentStatusChanged?: (agentStatus: any) => void;
  onCallStatusChanged?: (callStatus: any) => void;
  doCallTransfer?: (transferDetails: any) => void;
  allowOutbound?: boolean;
  allowInbound?: boolean;
}

const BREAK_REASONS = [
  { value: 10, label: "Bathroom", icon: <UserOutlined /> },
  { value: 20, label: "Break", icon: <CoffeeOutlined /> },
  { value: 30, label: "Meeting", icon: <UserOutlined /> },
  { value: 40, label: "Lunch", icon: <CoffeeOutlined /> },
  { value: 50, label: "End Shift", icon: <StopOutlined /> },
];

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

const ShaTelephony: React.FC<IShaTelephonyProps> = ({
  enableRejectCall,
  // providerType,
  // showDialpad = true,
  // showAvailabilityToggle = true,
  // config,
  onCallStarted,
  onCallEnded,
  onAgentStatusChanged,
  doCallTransfer,
  onCallStatusChanged,
  allowInbound = true,
  allowOutbound = true,
}) => {
  const { provider, providerState, getTelephonyAgent } = useTelephonyContext();

  // State management
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [loginForm] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [isInShift, setIsInShift] = useState(false);
  const [shiftStartTime, setShiftStartTime] = useState<Date | null>(null);
  const [shiftDuration, setShiftDuration] = useState(0);
  const [isDialpadOpen, setIsDialpadOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  // const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  // const [transferTarget, setTransferTarget] = useState('');
  const [callTime, setCallTime] = useState(0);
  const [timerId, setTimerId] = useState<NodeJS.Timeout | null>(null);
  const [shiftTimerId, setShiftTimerId] = useState<NodeJS.Timeout | null>(null);
  // const [isExpanded, setIsExpanded] = useState(false);
  const [showCallPopup, setShowCallPopup] = useState(false);
  const [callerPersonData, setCallerPersonData] = useState<any>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isTransferring] = useState(false);
  const [isCcpVisible, setIsCcpVisible] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [isCustomViewOpen, setIsCustomViewOpen] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState("1");

  // Ref to prevent double-answer taps
  const isAnswering = useRef(false);

  //const toggleCcp = () => setIsCcpVisible((prev) => !prev);

  const toggleHold = async () => {
    if (!provider) return;
    try {
      if (isOnHold) {
        await provider.unholdCall?.();
      } else {
        await provider.holdCall?.();
      }
      setIsOnHold(!isOnHold);
    } catch (err) {
      console.error("Hold toggle failed", err);
    }
  };

  // Person lookup
  const {
    refetch: fetchPersonByMobile,
    //data: personData,
    loading: isLoadingPerson,
  } = useGet<any>({
    path: `/api/dynamic/Shesha/Person/Crud/GetAll`,
    lazy: true,
  });

  //  lookup person by mobile number

  // Updated lookupPersonByMobile function
  const lookupPersonByMobile = useCallback(
    async (mobileNumber: string) => {
      // Normalize the phone number before lookup
      const normalizedPhone = normalizePhoneNumber(mobileNumber);
      const cleanedNumber = normalizedPhone.cleaned;

      console.log("Original number:", mobileNumber);
      console.log("Cleaned number for lookup:", cleanedNumber);

      const filter = { "==": [{ var: "mobileNumber1" }, cleanedNumber] };

      try {
        const response = await fetchPersonByMobile({
          queryParams: {
            filter: JSON.stringify(filter),
          },
        });

        const person = response?.result?.items?.[0];

        if (person) {
          const fullName =
            `${person.firstName ?? ""} ${person.lastName ?? ""}`.trim();

          provider?.setCallerName?.(fullName);
          setCallerPersonData(person);
        } else {
          console.warn("No person found for number:", cleanedNumber);
          // Try with 0 prefix if the first lookup failed
          if (cleanedNumber.startsWith("+27")) {
            const numberWith0 = "0" + cleanedNumber.substring(3); // Remove +27 and add 0
            const filterWith0 = {
              "==": [{ var: "mobileNumber1" }, numberWith0],
            };

            console.log("Trying lookup with 0 prefix:", numberWith0);

            const response2 = await fetchPersonByMobile({
              queryParams: {
                filter: JSON.stringify(filterWith0),
              },
            });

            const person2 = response2?.result?.items?.[0];
            if (person2) {
              const fullName =
                `${person2.firstName ?? ""} ${person2.lastName ?? ""}`.trim();
              provider?.setCallerName?.(fullName);
              setCallerPersonData(person2);
            } else {
              console.warn(
                "No person found in both lookups for:",
                mobileNumber
              );
            }
          }
        }
      } catch (err) {
        console.error("Error during lookup:", err);
      }
    },
    [fetchPersonByMobile, provider]
  );

  //  handleIncomingCallRinging function
  const handleIncomingCallRinging = useCallback(
    (incomingCall: any) => {
      const normalizedPhone = normalizePhoneNumber(incomingCall.callerNumber);
      lookupPersonByMobile(normalizedPhone.cleaned);
    },
    [lookupPersonByMobile]
  );

  const handleCallStarted = useCallback(
    (callDetails: any) => {
      if (onCallStarted) {
        onCallStarted(cloneDeep(callDetails));
      }
    },
    [onCallStarted]
  );

  const handleCallEnded = useCallback(
    (callDetails: any) => {
      if (onCallEnded) {
        onCallEnded(cloneDeep(callDetails));
      }
    },
    [onCallEnded]
  );

  const handleOnAgentStatusChanged = useCallback(
    (isAvailable: boolean) => {
      console.log("onAgentStatusChanged", onAgentStatusChanged);
      if (onAgentStatusChanged) {
        console.log("Agent status changed called:", isAvailable);
        onAgentStatusChanged({ isAvailable });
      }
    },
    [onAgentStatusChanged]
  );

  const handleOnCallStatusChanged = useCallback(
    (callStatus: any) => {
      console.log("onCallStatusChanged", onCallStatusChanged);
      if (onCallStatusChanged) {
        console.log("Call status changed called:", callStatus);
        onCallStatusChanged({ callStatus });
      }
    },
    [onCallStatusChanged]
  );

 

  //  useEffect for handling incoming calls
  useEffect(() => {
    if (
      providerState.status === CallStatus.Ringing &&
      providerState.currentCallDirection === CallDirection.Inbound &&
      providerState.callerNumber
    ) {
      // Normalize the caller number before lookup
      const normalizedPhone = normalizePhoneNumber(providerState.callerNumber);
      lookupPersonByMobile(normalizedPhone.cleaned);
    }
  }, [
    providerState.status,
    providerState.currentCallDirection,
    providerState.callerNumber,
  ]);

  useEffect(() => {
    if (provider && typeof provider.onIncomingCallRinging === "function") {
      provider.onIncomingCallRinging(handleIncomingCallRinging);
    }
    if (provider && typeof provider.onIncomingCallAnswered === "function") {
      provider.onIncomingCallAnswered(handleCallStarted);
    }
    if (provider && typeof provider.onOutboundCallAnswered === "function") {
      provider.onOutboundCallAnswered(handleCallStarted);
    }

    if (provider && typeof provider.onCallEnded === "function") {
      provider.onCallEnded(handleCallEnded);
    }

    if (provider && typeof provider.onAvailabilityChanged === "function") {
      provider.onAvailabilityChanged(handleOnAgentStatusChanged);
    }

    if (provider && typeof provider.onStatusChanged === "function") {
      provider.onStatusChanged(handleOnCallStatusChanged);
    }

    //providerState
  }, [provider]);
  //dynamically update the  ui for breaks or availibility
  useEffect(() => {
    if (!providerState.isLoggedIn) {
      if (isInShift) {
        setIsInShift(false);
        setShiftStartTime(null);
      }
      return;
    }

    if (providerState.isAvailable && !isInShift) {
      setIsInShift(true);
      setShiftStartTime(new Date());
    } else if (!providerState.isAvailable && isInShift) {
      if (providerState.status === CallStatus.Idle) {
        setIsInShift(false);
        setShiftStartTime(null);
      }
    }
  }, [
    providerState.isLoggedIn,
    providerState.isAvailable,
    isInShift,
    providerState.status,
  ]);
  useEffect(() => {
    const fetchSession = async () => {
      console.log("Fetching existing session for provider:", provider);
      if (provider && typeof provider.getExistingLogin === "function") {
        try {
          setIsLoading(true);
          const telephonyAgent = await getTelephonyAgent();
          provider.setLoginCredentials(
            telephonyAgent?.username || provider.config.username,
            null,
            null
          );

          const response = await provider.getExistingLogin();

          if (response) {
            providerState.isLoggedIn = true;
            providerState.sessionId = response.session;
            provider.sessionId = response.session;
            provider.setSession(response.session);
            if (response.isStarted) {
              setIsInShift(true);
              setShiftStartTime(new Date());
            }
          }
        } catch (error) {
          console.error("Error fetching existing session:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchSession();
    //distructore set session as null to deregister signalr
    return () => {
      if (provider) {
        provider.setSession(null);
        provider.release();
      }
    };
  }, [provider]);

  // Force CCP modal to be visible initially if provider supports popout
  // This ensures the #customviews container exists in DOM for AWS Connect
  useEffect(() => {
    if (provider && provider.supportsPopout) {
      setIsCcpVisible(false); // Keep it hidden visually but in DOM
    }
  }, [provider]);

  useEffect(() => {
    if (providerState.status !== CallStatus.InCall && timerId) {
      clearInterval(timerId);
      setTimerId(null);
      setCallTime(0);
    }
    if (providerState.status === CallStatus.InCall && !timerId) {
      const id = setInterval(() => {
        setCallTime((prev) => prev + 1);
      }, 1000);
      setTimerId(id);
    }
    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, [providerState.status, timerId]);

  useEffect(() => {
    if (isInShift && !shiftTimerId) {
      const id = setInterval(() => {
        if (shiftStartTime) {
          const duration = Math.floor(
            (new Date().getTime() - shiftStartTime.getTime()) / 1000
          );
          setShiftDuration(duration);
        }
      }, 1000);
      setShiftTimerId(id);
    }
    if (!isInShift && shiftTimerId) {
      clearInterval(shiftTimerId);
      setShiftTimerId(null);
      setShiftDuration(0);
    }
    return () => {
      if (shiftTimerId) clearInterval(shiftTimerId);
    };
  }, [isInShift, shiftTimerId, shiftStartTime]);

  // useEffect(() => {
  //   if (providerState.status === CallStatus.InCall && onCallStarted) {
  //     onCallStarted(providerState);
  //   }
  //   if (
  //     providerState.status === CallStatus.Idle &&
  //     callTime > 0 &&
  //     onCallEnded
  //   ) {
  //     onCallEnded(providerState);
  //   }
  // }, [
  //   providerState.status,
  //   callTime,
  //   onCallStarted,
  //   onCallEnded,
  //   providerState,
  // ]);
  useEffect(() => {
    if (
      (providerState.status === CallStatus.InCall ||
        providerState.status === CallStatus.Awaiting) &&
      provider?.supportsPopout
    ) {

      if (provider.supportsCustomViews) {
        setIsCustomViewOpen(true);
        setActiveTabKey("2");
      }

      // Only show the modal when status is Awaiting
      if (providerState.status === CallStatus.Awaiting) {
        setIsCcpVisible(true);
      }
    }
  }, [providerState.status, provider?.supportsPopout]);
  useEffect(() => {
    if (
      allowInbound &&
      providerState.status === CallStatus.Ringing &&
      providerState.currentCallDirection === CallDirection.Inbound
    ) {
      setShowCallPopup(true);
    } else {
      setShowCallPopup(false);
    }
  }, [providerState.status, providerState.currentCallDirection, allowInbound]);

  // Handlers
  const handleStartShift = useCallback(async () => {
    if (!provider || !providerState.isLoggedIn) return;
    //let session = { sessionId: provider.sessionId };
    let agentSession = await provider.setAvailable();
    console.log("agentSession", agentSession);
    setIsInShift(true);
    setShiftStartTime(new Date());
  }, [provider, providerState.isLoggedIn]);

  const handleEndShift = useCallback(
    async (reasonCode: number) => {
      if (!provider || !isInShift) return;
      if (
        reasonCode === 50 &&
        typeof (provider as any).endShift === "function"
      ) {
        const success = await (provider as any).endShift(reasonCode);
        if (success) {
          setIsInShift(false);
          setShiftStartTime(null);
        }
      } else {
        if (typeof provider.setOnBreakWithReason === "function") {
          await provider.setOnBreakWithReason(reasonCode);
        } else {
          await provider.setOnBreak();
        }
        setIsInShift(false);
        setShiftStartTime(null);
      }
    },
    [provider, isInShift]
  );

  const handleLogin = useCallback(
    async (values: any) => {
      if (!provider) return;
      setIsLoading(true);
      let telephonyAgent = null;

      try {
        if (provider.shouldShowLoginDialog()) {
          telephonyAgent = await getTelephonyAgent();
          if (!telephonyAgent) {
            loginForm.setFields([
              {
                name: "station",
                errors: [
                  "Telephony agent not found. Please check your configuration.",
                ],
              },
            ]);
            return;
          }
        }
        if (provider && typeof provider.setLoginCredentials === "function") {
          provider.setLoginCredentials(
            telephonyAgent?.username || provider.config.username,
            telephonyAgent?.password || provider.config.password,
            values.station
          );
        }
        const response = await provider.agentLogin();
        console.log("agent login response", response);
        if (response.result) {
          providerState.isLoggedIn = true;
          providerState.sessionId = response.session;
          provider.sessionId = response.session;
          provider.setSession(response.session);
          setIsLoginModalOpen(false);
          loginForm.resetFields();
          //try to ser available
          try {
            await handleStartShift();
          } catch (error) {
            console.error("Failed to set available after login:", error);
            // Optionally, show a warning to the user or retry after a delay
            // You might want to set a state variable to show a warning message
          }
        } else {
          loginForm.setFields([
            {
              name: "station",
              errors: ["Login failed. Please check your credentials."],
            },
          ]);
        }
      } catch (error) {
        console.error("Login error:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [provider, loginForm]
  );

  const handleLogout = useCallback(async () => {
    if (!provider) return;
    if (isInShift) {
      await handleEndShift(50);
    }
    setIsCcpVisible(false);
    await provider.logout();
  }, [provider, isInShift]);


  const isDialPadDisabled = (): boolean => {
    return !provider;
  };

  const loginButtonClick = async () => {
    if (!provider) return;
    if (provider.shouldShowLoginDialog()) {
      setIsLoginModalOpen(true);
      return;
    } else {
      await handleLogin({ station: "123" });
    }
  };

  //muting
  const toggleMute = useCallback(async () => {
    if (!provider) return;
    try {
      if (isMuted) {
        await provider.unmuteCall?.();
      } else {
        await provider.muteCall?.();
      }
      setIsMuted((prev) => !prev);
    } catch (err) {
      console.error("Mute/unmute failed:", err);
    }
  }, [provider, isMuted]);

  const handleDial = useCallback(() => {
    if (phoneNumber && provider) {
      provider.dial(phoneNumber);
      setIsDialpadOpen(false);
      setPhoneNumber("");
    }
  }, [phoneNumber, provider]);

  const handleTransferNumber = useCallback(() => {
    if (phoneNumber && provider) {
      provider.transferCall(phoneNumber, false)
      .then(() => {
        setIsDialpadOpen(false);
        setPhoneNumber("");
      })
      .catch((err) => {
        console.error("Transfer failed:", err);
        // Optionally show error notification to user
      });
    }
  }, [phoneNumber, provider]);

  // const handleTransfer = useCallback(async () => {
  //   if (!provider) {
  //     console.warn("Provider or session ID missing");
  //     return;
  //   }

  //   try {
  //     await provider.transferCaller?.();
  //     console.log("Transfer call request sent.");
  //   } catch (err) {
  //     console.error("Transfer failed:", err);
  //   }
  // }, [provider, providerState.sessionId]);

   const handleTransfer = useCallback(
    (transferDetails: any) => {
      console.log("handleTransfer called with:", transferDetails);
      if (doCallTransfer) {
        doCallTransfer(transferDetails);
      } else {
        //open the dialpad for number input
        setIsDialpadOpen(true);
      }
    },
    [doCallTransfer]
  );

  const handleAnswer = useCallback(async () => {
    // Guard against double-answer taps
    if (isAnswering.current) return;

    try {
      isAnswering.current = true;
      await provider?.answerCall?.();
      setShowCallPopup(false);
    } catch (err) {
      console.error("Answer failed:", err);
    } finally {
      isAnswering.current = false;
    }
  }, [provider, providerState.currentCallDirection]);

  const handleReject = useCallback(() => {
    provider?.rejectCall();
    setShowCallPopup(false);
    setCallerPersonData(null);
  }, [provider]);

  const getBadgeStatus = (): "success" | "warning" | "error" | "default" => {
    if (!providerState.isLoggedIn) return "default";
    if (providerState.status === CallStatus.InCall) return "error";
    if (providerState.isAvailable && isInShift) return "success";
    if (providerState.isLoggedIn && !isInShift) return "warning";
    return "default";
  };

  const getStatusText = (): string => {
    if (!providerState.isLoggedIn) return "Offline";
    if (providerState.status === CallStatus.InCall) return "In Call";
    if (providerState.status === CallStatus.Ringing) return "Ringing";
    if (providerState.status === CallStatus.Awaiting) return "After Call Work";
    if (isInShift) return "Available";
    return "Logged In";
  };

  const renderKeypadButtons = () => {
    const buttons = [
      "1",
      "2",
      "3",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      "*",
      "0",
      "#",
    ];
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 12,
          marginTop: 20,
          padding: "0 8px",
        }}
      >
        {buttons.map((btn) => (
          <Button
            key={btn}
            style={{
              height: 45,
              fontSize: 18,
              fontWeight: "bold",
              border: "1px solid #d9d9d9",
              borderRadius: "6px",
              backgroundColor: "#fafafa",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#e6f7ff";
              e.currentTarget.style.borderColor = "#91d5ff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#fafafa";
              e.currentTarget.style.borderColor = "#d9d9d9";
            }}
            onClick={() => setPhoneNumber((prev) => prev + btn)}
          >
            {btn}
          </Button>
        ))}
      </div>
    );
  };

  const breakMenuItems = BREAK_REASONS.map((reason) => ({
    key: reason.value,
    label: reason.label,
    icon: reason.icon,
    onClick: () => handleEndShift(reason.value),
  }));

  // Compact Status Display
  const StatusDisplay = () => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        minWidth: 140,
        padding: "4px 8px",
        borderRadius: "6px",
        backgroundColor: "#fafafa",
        border: "1px solid #f0f0f0",
      }}
    >
      <Badge status={getBadgeStatus()} />
      <Text strong style={{ fontSize: 12, color: "#262626" }}>
        {getStatusText()}
      </Text>
      {isInShift && (
        <Text
          style={{
            fontSize: 11,
            color: "#8c8c8c",
            fontFamily: "monospace",
            backgroundColor: "#f0f0f0",
            padding: "2px 6px",
            borderRadius: "4px",
          }}
        >
          {formatTime(shiftDuration)}
        </Text>
      )}
    </div>
  );

  const DropDownMenuItems = (): any => {
    var menuItems = [];

    if (allowOutbound) {
      menuItems.push({
        key: "dialpad",
        label: "Open Dialpad",
        icon: <PhoneOutlined />,
        onClick: () => setIsDialpadOpen(true),
        // Disable if provider is not available
        disabled: isDialPadDisabled(),
      });
    }
    if (isInShift) {
      if (allowInbound) {
        menuItems.push({
          key: "break",
          label: "Break / End Shift",
          icon: <CoffeeOutlined />,
          children: breakMenuItems,
        });
      }
    } else if (allowInbound) {
      menuItems.push({
        key: "start-shift",
        label: "Start Shift",
        icon: <PlayCircleOutlined />,
        onClick: handleStartShift,
      });
    }

    menuItems.push({
      key: "divider",
      type: "divider",
    });
    menuItems.push({
      key: "logout",
      label: "Logout",
      icon: <LogoutOutlined />,
      onClick: handleLogout,
      danger: true,
    });

    return menuItems;
  };

  const IncomingCallPopup = () => {
    if (!showCallPopup) return null;

    console.log("Enable Reject Call:", enableRejectCall);

    const displayName = callerPersonData
      ? `${callerPersonData.firstName || ""} ${callerPersonData.lastName || ""}`.trim()
      : providerState.callerName || "Unknown";

    return (
      <div
        style={{
          position: "fixed",
          top: "20px",
          right: "20px",
          zIndex: 9999,
          background: "rgba(14, 210, 106, 0.77)",
          borderRadius: "12px",
          padding: "16px 18px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.1)",
          minWidth: "280px",
          color: "#fff",
          fontSize: "13px",
          border: "1px solid rgba(255,255,255,0.2)",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Close Button */}
        <div style={{ position: "absolute", top: 8, right: 8 }}>
          <Button
            type="text"
            size="small"
            onClick={handleReject}
            style={{
              color: "#fff",
              backgroundColor: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "6px",
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            icon={<CloseCircleOutlined style={{ fontSize: "14px" }} />}
          />
        </div>

        {/* Caller Info */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <Avatar
            size={40}
            style={{
              backgroundColor: "#fff",
              color: "#52c41a",
              fontSize: 18,
              border: "2px solid rgba(255,255,255,0.3)",
            }}
            icon={<PhoneFilled />}
          />
          <div style={{ flex: 1 }}>
            <div
              style={{ fontWeight: 600, fontSize: "14px", marginBottom: "2px" }}
            >
              Incoming Call
              {isLoadingPerson && (
                <span style={{ marginLeft: "8px", fontSize: "12px" }}>
                  Looking up...
                </span>
              )}
            </div>
            <div style={{ fontSize: "13px", opacity: 0.95 }}>
              {providerState.callerNumber}
            </div>
            <div style={{ fontSize: "11px", opacity: 0.85, marginTop: "2px" }}>
              {displayName}
            </div>
            {callerPersonData?.email && (
              <div style={{ fontSize: "10px", opacity: 0.8, marginTop: "2px" }}>
                {callerPersonData.email}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            type="default"
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={handleAnswer}
            style={{
              flex: 1,
              backgroundColor: "#fff",
              color: "#52c41a",
              border: "none",
              fontWeight: 600,
              borderRadius: "8px",
              height: "36px",
              fontSize: "13px",
            }}
          >
            Answer
          </Button>
          {enableRejectCall && (
            <Button
              type="text"
              size="small"
              icon={<CloseCircleOutlined />}
              onClick={handleReject}
              style={{
                flex: 1,
                border: "1px solid rgba(255,255,255,0.4)",
                color: "#fff",
                fontWeight: 600,
                borderRadius: "8px",
                height: "36px",
                fontSize: "13px",
                backgroundColor: "rgba(255,255,255,0.1)",
              }}
            >
              Reject
            </Button>
          )}
        </div>
      </div>
    );
  };

  // Call Info Display
  const CallInfoDisplay = () => {
    if (providerState.status === CallStatus.Idle) return null;

    const displayName = callerPersonData
      ? `${callerPersonData.firstName || ""} ${callerPersonData.lastName || ""}`.trim()
      : providerState.callerName || "Unknown";

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          maxWidth: 260,
          padding: "6px 10px",
          backgroundColor: "#fffbe6",
          border: "1px solid #ffe58f",
          borderRadius: "6px",
        }}
      >
        <Avatar
          size={24}
          icon={<PhoneOutlined />}
          style={{ backgroundColor: "#faad14" }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text
            ellipsis
            style={{ fontSize: 12, fontWeight: 500, color: "#262626" }}
          >
            {providerState.callerNumber}
            {displayName && displayName !== "Unknown" && ` (${displayName})`}
          </Text>
          {(providerState.status === CallStatus.InCall ||
            providerState.status === CallStatus.Ringing) && (
            <div style={{ marginTop: "2px", display: "flex", gap: 8 }}>
              {providerState.status === CallStatus.InCall && (
                <Text
                  style={{
                    fontSize: 11,
                    color: "#8c8c8c",
                    fontFamily: "monospace",
                    backgroundColor: "#f0f0f0",
                    padding: "1px 4px",
                    borderRadius: "3px",
                  }}
                >
                  {formatTime(callTime)}
                </Text>
              )}
              {providerState.status === CallStatus.Ringing &&
                providerState.currentCallDirection ===
                  CallDirection.Outbound && (
                  <Button
                    size="small"
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={() => provider?.endCall?.()}
                    style={{
                      fontSize: 11,
                      padding: "2px 6px",
                      borderRadius: "4px",
                      height: "auto",
                      lineHeight: "14px",
                    }}
                  ></Button>
                )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Main compact view
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "8px 12px",
        backgroundColor: "#fff",
        borderRadius: "8px",
      }}
    >
      {provider && provider.supportsPopout && providerState.isLoggedIn && (
        <Tooltip title="AWS Popout">
          <Button
            size="small"
            icon={
              isCcpVisible || isCustomViewOpen ? (
                <UpCircleOutlined />
              ) : (
                <DownCircleOutlined />
              )
            }
            onClick={() =>
              setIsCcpVisible((prev) => {
                const next = !prev;
                setIsCustomViewOpen(next);
                return next;
              })
            }
            style={{
              borderRadius: "6px",
              fontWeight: 500,
              zIndex: 9999,
            }}
          />
        </Tooltip>
      )}

      {provider && provider.supportsPopout && (
        <div
          id="ccp-container"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: isCcpVisible
              ? "rgba(0, 0, 0, 0.3)"
              : "transparent",
            zIndex: isCcpVisible ? 9999 : -1,
            display: isCustomViewOpen ? "flex" : "none",
            visibility: isCcpVisible ? "visible" : "hidden",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            boxSizing: "border-box",
            pointerEvents: isCcpVisible ? "auto" : "none",
          }}
        >
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "400px",
              height: "100%",
              maxHeight: "120vh",
              backgroundColor: "#fff",
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
              padding: "12px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Close */}
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => {
                setIsCcpVisible(false);
                setIsCustomViewOpen(false);
              }}
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 10000,
                backgroundColor: "#fff",
                border: "1px solid #d9d9d9",
                width: 28,
                height: 28,
                padding: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
              }}
            />

            <Tabs
              activeKey={activeTabKey}
              onChange={setActiveTabKey}
              style={{ marginTop: 32, flex: 1 }}
              items={[
                {
                  key: "1",
                  label: "CCP Dialer",
                  children: (
                    <div
                      id="ccp"
                      style={{
                        flex: 1,
                        border: "1px solid #f0f0f0",
                        borderRadius: "8px",
                        overflow: "scroll",
                        height: "calc(100vh - 160px)",
                      }}
                    >
                      {/* CCP iframe mounts here */}
                    </div>
                  ),
                },
                {
                  key: "2",
                  label: "Custom View",
                  children: (
                    <div
                      style={{
                        flex: 1,
                        border: "1px solid #f0f0f0",
                        borderRadius: "8px",
                        overflow: "scroll",
                        height: "calc(100vh - 160px)",
                        textAlign: "center",
                        position: "relative",
                      }}
                    >
                      <div
                        id="customviews-placeholder"
                        style={{ padding: "16px" }}
                      >
                        <p>
                          This area is empty until the call is completed.
                          Contact Qualifications will appear here afterward.
                        </p>

                        <p style={{ fontSize: "12px", color: "#8c8c8c" }}>
                          Content is populated automatically by AWS Connect once
                          the call ends.
                        </p>
                      </div>
                      <div
                        id="customviews"
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                        }}
                      />
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </div>
      )}
      {/* Status Display */}
      <StatusDisplay />
      {/* Call Info */}
      <CallInfoDisplay />
      <IncomingCallPopup />
      <Tooltip title="Transfer">
        <Button
          size="small"
          icon={<UserSwitchOutlined />}
          onClick={handleTransfer}
          hidden={providerState.status !== CallStatus.InCall}
          loading={isTransferring}
          style={{ borderRadius: "6px" }}
        />
      </Tooltip>
      {/* Primary Action Button */}
      {!providerState.isLoggedIn ? (
        <Tooltip
          title={
            !allowInbound && !allowOutbound
              ? "No telephony actions are allowed. Please contact your administrator."
              : "Login telephony"
          }
        >
          <Button
            size="small"
            type="primary"
            icon={<LoginOutlined />}
            loading={isLoading}
            onClick={() => loginButtonClick()}
            style={{ borderRadius: "6px", fontWeight: 500 }}
            disabled={!allowInbound && !allowOutbound}
          >
            Login
          </Button>
        </Tooltip>
      ) : providerState.status === CallStatus.InCall ? (
        <Space size="small">
          <Tooltip title={isMuted ? "Unmute" : "Mute"}>
            <Button
              size="small"
              icon={isMuted ? <AudioMutedOutlined /> : <AudioOutlined />}
              onClick={toggleMute}
              style={{ borderRadius: "6px" }}
            />
          </Tooltip>
          <Tooltip title={isOnHold ? "Unhold" : "Hold"}>
            <Button
              size="small"
              icon={isOnHold ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
              onClick={toggleHold}
              style={{ borderRadius: "6px" }}
            />
          </Tooltip>

          <Tooltip title="Hang Up">
            <Button
              size="small"
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => provider?.endCall()}
              style={{ borderRadius: "6px" }}
            />
          </Tooltip>
          <Tooltip title="Transfer">
            <Button
              size="small"
              icon={<UserSwitchOutlined />}
              onClick={handleTransfer}
              loading={isTransferring}
              disabled={
                isTransferring || providerState.status !== CallStatus.InCall
              }
              style={{ borderRadius: "6px" }}
              hidden={true}
            />
          </Tooltip>
        </Space>
      ) : providerState.status === CallStatus.Awaiting ? (
        <Button
          size="small"
          type="primary"
          onClick={() => provider?.finaliseCall()}
          style={{ borderRadius: "6px", fontWeight: 500 }}
        >
          Finalise
        </Button>
      ) : null}
      {/* More Actions Dropdown */}
      {providerState.isLoggedIn && DropDownMenuItems().length > 0 && (
        <Dropdown
          menu={{
            items: DropDownMenuItems(),
          }}
          trigger={["click"]}
        >
          <Button
            size="small"
            icon={<MoreOutlined />}
            style={{ borderRadius: "6px" }}
          />
        </Dropdown>
      )}
      {/* Modals */}
      {isLoginModalOpen && (
        <Modal
          title={
            <div
              style={{ fontSize: "16px", fontWeight: 600, color: "#262626" }}
            >
              Login to Telephony
            </div>
          }
          open={isLoginModalOpen}
          onCancel={() => setIsLoginModalOpen(false)}
          footer={null}
          width={420}
          style={{ top: "20%" }}
          destroyOnClose={true}
        >
          <div style={{ padding: "8px 0" }}>
            <Form form={loginForm} onFinish={handleLogin} layout="vertical">
              <Form.Item
                name="station"
                label="Station"
                rules={[
                  { required: true, message: "Please enter your station" },
                ]}
              >
                <Input
                  placeholder="e.g. 6990"
                  style={{ borderRadius: "6px" }}
                  size="large"
                />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={isLoading}
                  style={{ borderRadius: "6px", fontWeight: 500 }}
                  size="large"
                >
                  Login
                </Button>
              </Form.Item>
            </Form>
          </div>
        </Modal>
      )}
      <Modal
        title={
          <div style={{ fontSize: "16px", fontWeight: 600, color: "#262626" }}>
            Dial Number
          </div>
        }
        open={isDialpadOpen}
        onCancel={() => setIsDialpadOpen(false)}
        footer={[
          <Button
            key="cancel"
            onClick={() => setIsDialpadOpen(false)}
            style={{ borderRadius: "6px" }}
          >
            Cancel
          </Button>,
          <Button
            key="transfer"
            type="primary"
            onClick={handleTransferNumber}
            disabled={!phoneNumber || providerState.status !== CallStatus.InCall}
            style={{ borderRadius: "6px", fontWeight: 500 }}
          >
            Transfer
          </Button>,
          <Button
            key="dial"
            type="primary"
            onClick={handleDial}
            disabled={!phoneNumber || providerState.status === CallStatus.InCall}            style={{ borderRadius: "6px", fontWeight: 500 }}
          >
            Dial
          </Button>,
        ]}
        width={380}
        style={{ top: "20%" }}
      >
        <div style={{ padding: "8px 0" }}>
          <Input
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            addonBefore={<PhoneOutlined />}
            placeholder="Enter phone number"
            style={{ borderRadius: "6px", marginBottom: "8px" }}
            size="large"
          />
          {renderKeypadButtons()}
        </div>
      </Modal>
    </div>
  );
};

export default ShaTelephony;
